import React, { useState, useEffect, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { app } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  addDoc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { alpha, useTheme } from "@mui/material/styles";
import { MuiTelInput } from "mui-tel-input";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { DataGrid } from "@mui/x-data-grid";
import PropertyContext from "../contexts/PropertyContext";

// ── Constants & helpers ───────────────────────────────────────────────────────

const CALL_STATUS = {
  ACTIVE: "active",
  CHECKED_IN: "checked_in",
  REMOVED: "removed",
  MARKED_FOR_DELETION: "marked_for_deletion",
};

const getTodayKey = () => dayjs().format("YYYY-MM-DD");

// Normalize to last 10 digits — used as the Calls document ID
const phoneToDocId = (phone) => (phone || "").replace(/\D/g, "").slice(-10);

const formatPhoneNumber = (value) => {
  const digits = (value || "").replace(/\D/g, "").slice(-10);
  const match = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) return value || "";
  return `(${match[1]}) ${match[2]}-${match[3]}`;
};

const getLastNote = (value) => {
  if (!value) return "";
  const lines = value.split("\n");
  return lines[lines.length - 1];
};

const isValidPhone = (value) => (value || "").replace(/\D/g, "").length >= 10;

const buildInitials = (userInfo) =>
  userInfo?.fullName
    ? userInfo.fullName.split(" ").map((n) => n[0]).join(".").concat(".")
    : "";

// Write a single event to the callLog collection
const writeCallLog = (clientId, propertyId, entry) =>
  addDoc(propertyCollection(clientId, propertyId, "callLog"), {
    timestamp: serverTimestamp(),
    date: getTodayKey(),
    ...entry,
  });

// ── Request Removal Modal ─────────────────────────────────────────────────────

const RequestRemovalModal = ({ isOpen, onClose, callerData }) => {
  const [notes, setNotes] = useState("");
  const [selection, setSelection] = useState("");
  const [saving, setSaving] = useState(false);

  const selections = ["Checked In", "Requested Removal", "Other"];
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  const handleClose = () => {
    setSelection("");
    setNotes("");
    setSaving(false);
    onClose();
  };

  const markForDeletion = async () => {
    if (!selection) { alert("Please select a reason."); return; }
    if (!callerData?.id) { alert("Missing caller data."); return; }

    try {
      setSaving(true);
      const callerDoc = propertyDoc(selectedClient, selectedProperty, "Calls", callerData.id);

      if (selection === "Checked In") {
        await updateDoc(callerDoc, { checkInDate: getTodayKey(), status: CALL_STATUS.CHECKED_IN });
      } else if (selection === "Requested Removal") {
        await updateDoc(callerDoc, { status: CALL_STATUS.REMOVED });
      } else {
        await updateDoc(callerDoc, { status: CALL_STATUS.MARKED_FOR_DELETION, notes });
      }

      handleClose();
    } catch (err) {
      console.error("Error updating caller:", err);
      alert("Something went wrong.");
      setSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", width: "50%",
        bgcolor: "background.paper", boxShadow: 24, p: 4,
        overflow: "auto", maxHeight: "90%",
      }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Select value={selection} onChange={(e) => setSelection(e.target.value)} fullWidth displayEmpty>
              <MenuItem value="" disabled>Select a reason</MenuItem>
              {selections.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              fullWidth multiline rows={4} />
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", mt: 2, justifyContent: "space-between" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={markForDeletion} disabled={saving}>
            {saving ? "Saving..." : "Confirm"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ── Non-Lead Call Modal ───────────────────────────────────────────────────────

const NonLeadCallModal = ({ isOpen, onClose, userInfo }) => {
  const [selection, setSelection] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const selections = ["Current Guest", "Employee", "Not Interested", "Wrong Number", "Other"];
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  const handleClose = () => {
    setSelection("");
    setNotes("");
    setSaving(false);
    onClose();
  };

  const addNonLeadCall = async () => {
    if (!selection) { alert("Please select a call type."); return; }
    try {
      setSaving(true);
      await writeCallLog(selectedClient, selectedProperty, {
        type: "non-lead",
        subType: selection,
        notes,
        agentId: userInfo?.id || null,
        agentInitials: buildInitials(userInfo),
        answered: true,
        callerDocId: null,
        callerName: null,
        phoneNumber: null,
        callSid: null,
      });
      handleClose();
    } catch (err) {
      console.error("Error logging non-lead call:", err);
      alert("Something went wrong.");
      setSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", width: "50%",
        bgcolor: "background.paper", boxShadow: 24, p: 4,
        overflow: "auto", maxHeight: "90%",
      }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Select value={selection} onChange={(e) => setSelection(e.target.value)} fullWidth displayEmpty>
              <MenuItem value="" disabled>Select call type</MenuItem>
              {selections.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              fullWidth multiline rows={4} />
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", mt: 2, justifyContent: "space-between" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={addNonLeadCall} disabled={saving}>
            {saving ? "Saving..." : "Log Call"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ── Call Modal ────────────────────────────────────────────────────────────────

const CallModal = ({ isOpen, onClose, callerData, userInfo, prefillPhone = "" }) => {
  const [requestRemovalOpen, setRequestRemovalOpen] = useState(false);

  const [name, setName] = useState("");
  const [nextCallDate, setNextCallDate] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [tourDate, setTourDate] = useState("");
  const [tourTime, setTourTime] = useState("");
  const [checkInDate, setCheckInDate] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [pastNotes, setPastNotes] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [callCount, setCallCount] = useState(0);
  const [saving, setSaving] = useState(false);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  useEffect(() => {
    if (!isOpen) return;

    if (!callerData) {
      setName("");
      setNextCallDate("");
      setPhoneNumber(prefillPhone || "");
      setTourDate("");
      setTourTime("");
      setCheckInDate("");
      setCheckInTime("");
      setPastNotes("");
      setNewNotes("");
      setCallCount(0);
      setSaving(false);
      return;
    }

    setName(callerData.name || "");
    setPhoneNumber(callerData.phoneNumber || "");
    setTourDate(callerData.tourDate || "");
    setTourTime(callerData.tourTime || "");
    setCheckInDate(callerData.checkInDate || "");
    setCheckInTime(callerData.checkInTime || "");
    setPastNotes(callerData.pastNotes || "");
    setNewNotes("");
    setNextCallDate(callerData.nextCallDate || "");
    setCallCount(callerData.callCount || 0);
    setSaving(false);
  }, [isOpen, callerData]);

  const handleClose = () => {
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) { alert("Name is required."); return; }
    if (!phoneNumber.trim()) { alert("Phone number is required."); return; }
    if (!isValidPhone(phoneNumber)) { alert("Please enter a valid phone number."); return; }

    try {
      setSaving(true);

      const todayKey = getTodayKey();
      const initials = buildInitials(userInfo);
      const docId = phoneToDocId(phoneNumber);
      const callerRef = propertyDoc(selectedClient, selectedProperty, "Calls", docId);
      const noteLine = newNotes ? `${todayKey} - ${newNotes} - ${initials}` : "";
      const newCallCount = callCount + 1;

      if (callerData) {
        // ── Update existing profile ───────────────────────────────────────
        await updateDoc(callerRef, {
          name,
          phoneNumber,
          nextCallDate,
          tourDate,
          tourTime,
          checkInDate,
          checkInTime,
          pastNotes: noteLine
            ? (pastNotes ? `${pastNotes}\n${noteLine}` : noteLine)
            : pastNotes,
          tourAgent: callerData.tourAgent || (!callerData.tourDate && tourDate ? userInfo?.id : null),
          checkInAgent: callerData.checkInAgent || (!callerData.checkInDate && checkInDate ? userInfo?.id : null),
          callCount: newCallCount,
        });

        // Log outbound call event
        await writeCallLog(selectedClient, selectedProperty, {
          type: "outbound-manual",
          phoneNumber,
          callerDocId: docId,
          callerName: name,
          notes: newNotes,
          agentId: userInfo?.id || null,
          agentInitials: initials,
          answered: true,
          callSid: null,
        });
      } else {
        // ── Create or reactivate profile ──────────────────────────────────
        // Check if a record already exists for this phone number
        const existingSnap = await getDoc(callerRef);
        const existing = existingSnap.exists() ? existingSnap.data() : null;

        await setDoc(callerRef, {
          name,
          phoneNumber,
          // Preserve originalCallDate if reactivating
          originalCallDate: existing?.originalCallDate || todayKey,
          nextCallDate,
          tourDate,
          tourTime,
          checkInDate,
          checkInTime,
          pastNotes: noteLine,
          callAgent: existing?.callAgent || userInfo?.id || null,
          tourAgent: tourDate ? userInfo?.id : null,
          checkInAgent: checkInDate ? userInfo?.id : null,
          callCount: newCallCount,
          status: CALL_STATUS.ACTIVE,
        });

        // Log new lead event
        await writeCallLog(selectedClient, selectedProperty, {
          type: "new-lead",
          phoneNumber,
          callerDocId: docId,
          callerName: name,
          notes: newNotes,
          agentId: userInfo?.id || null,
          agentInitials: initials,
          answered: true,
          callSid: null,
        });
      }

      handleClose();
    } catch (err) {
      console.error("Error saving call:", err);
      alert("Something went wrong while saving.");
      setSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", width: "80%",
        bgcolor: "background.paper", boxShadow: 24, p: 4,
        overflow: "auto", maxHeight: "90%",
      }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Name" value={name} variant="filled"
              onChange={(e) => setName(e.target.value)} fullWidth />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Next Call Date" fullWidth
                value={nextCallDate ? dayjs(nextCallDate) : null}
                onChange={(v) => setNextCallDate(v ? v.format("YYYY-MM-DD") : "")}
                sx={{ mt: "2rem" }} />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6}>
            <MuiTelInput
              label="Phone Number"
              defaultCountry="US"
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value)}
              fullWidth
              error={phoneNumber.length > 0 && !isValidPhone(phoneNumber)}
              helperText={
                phoneNumber.length > 0 && !isValidPhone(phoneNumber)
                  ? "Enter a valid phone number"
                  : ""
              }
            />
            <Box sx={{ mt: "2rem" }}>
              {callerData && (
                <Button onClick={() => setRequestRemovalOpen(true)}>
                  Request Removal
                </Button>
              )}
              <RequestRemovalModal
                isOpen={requestRemovalOpen}
                onClose={() => { setRequestRemovalOpen(false); handleClose(); }}
                callerData={callerData}
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Tour Date" fullWidth
                value={tourDate ? dayjs(tourDate) : null}
                onChange={(v) => setTourDate(v ? v.format("YYYY-MM-DD") : "")} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker label="Tour Time"
                value={tourTime ? dayjs(tourTime, "HH:mm") : null}
                onChange={(v) => setTourTime(v ? v.format("HH:mm") : "")} fullWidth />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker label="Check-in Date"
                value={checkInDate ? dayjs(checkInDate) : null} fullWidth
                onChange={(v) => setCheckInDate(v ? v.format("YYYY-MM-DD") : "")} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker label="Check-in Time"
                value={checkInTime ? dayjs(checkInTime, "HH:mm") : null}
                onChange={(v) => setCheckInTime(v ? v.format("HH:mm") : "")} fullWidth />
            </LocalizationProvider>
          </Grid>

          {pastNotes && (
            <Grid item xs={6}>
              <TextField label="Past Notes" multiline rows={6} value={pastNotes}
                variant="outlined" InputProps={{ readOnly: true }} fullWidth />
            </Grid>
          )}
          <Grid item xs={pastNotes ? 6 : 12}>
            <TextField label="New Notes" multiline rows={6} value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)} variant="outlined" fullWidth />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", mt: "2rem", justifyContent: "space-between" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : callerData ? "Update Caller" : "Add to Call List"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ── CallList ──────────────────────────────────────────────────────────────────

const CallList = ({ userInfo }) => {
  const theme = useTheme();
  const location = useLocation();

  const [callsLoading, setCallsLoading] = useState(true);
  const [checkinList, setCheckinList] = useState([]);
  const [tourList, setTourList] = useState([]);
  const [callTodayList, setCallTodayList] = useState([]);
  const [allCalls, setAllCalls] = useState([]);

  // Stats derived from the callLog listener
  const [todayLog, setTodayLog] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sectionFilter, setSectionFilter] = useState("All");
  const [viewTab, setViewTab] = useState(0);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callerData, setCallerData] = useState(null);
  const [prefillPhone, setPrefillPhone] = useState("");
  const [nonLeadCallModalOpen, setNonLeadCallModalOpen] = useState(false);

  const [callInProgress, setCallInProgress] = useState(null);
  const [callSnack, setCallSnack] = useState({ open: false, message: "", severity: "info" });

  const handleCallModalOpen = (selectedCaller = null) => {
    setCallerData(selectedCaller);
    setCallModalOpen(true);
  };

  const handleCallModalClose = () => {
    setCallerData(null);
    setPrefillPhone("");
    setCallModalOpen(false);
  };

  // Handle navigation from IncomingCallListener toast
  useEffect(() => {
    const state = location.state;
    if (!state) return;

    if (state.openCallId) {
      setAllCalls((prev) => {
        const match = prev.find((c) => c.id === state.openCallId);
        if (match) {
          setCallerData(match);
          setCallModalOpen(true);
          window.history.replaceState({}, "");
        }
        return prev;
      });
    }

    if (state.prefillPhone) {
      setPrefillPhone(state.prefillPhone);
      setCallerData(null);
      setCallModalOpen(true);
      window.history.replaceState({}, "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ── Firestore listeners ───────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedProperty) return;
    setCallsLoading(true);

    const activeCallsQuery = query(
      propertyCollection(selectedClient, selectedProperty, "Calls"),
      where("status", "==", CALL_STATUS.ACTIVE)
    );

    const unsubscribeCalls = onSnapshot(activeCallsQuery, (snap) => {
      const todayKey = getTodayKey();
      const checkins = [], tours = [], callsToday = [], all = [];

      snap.forEach((d) => {
        const call = { ...d.data(), id: d.id };
        all.push(call);
        if (call.checkInDate === todayKey) checkins.push(call);
        else if (call.tourDate === todayKey) tours.push(call);
        else if (call.nextCallDate === todayKey) callsToday.push(call);
      });

      setCheckinList(checkins);
      setTourList(tours);
      setCallTodayList(callsToday);
      setAllCalls(all);

      // Retry openCallId navigation after data loads
      const pendingId = location.state?.openCallId;
      if (pendingId) {
        const match = all.find((c) => c.id === pendingId);
        if (match) {
          setCallerData(match);
          setCallModalOpen(true);
          window.history.replaceState({}, "");
        }
      }
    });

    // Today's call log — drives the stats panel
    const callLogQuery = query(
      propertyCollection(selectedClient, selectedProperty, "callLog"),
      where("date", "==", getTodayKey())
    );

    const unsubscribeLog = onSnapshot(callLogQuery, (snap) => {
      setTodayLog(snap.docs.map((d) => d.data()));
      setCallsLoading(false);
    });

    return () => {
      unsubscribeCalls();
      unsubscribeLog();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  // ── Click-to-call ─────────────────────────────────────────────────────────

  const handleClickToCall = async (e, row) => {
    e.stopPropagation();
    if (callInProgress) return;

    setCallInProgress(row.id);
    try {
      const functions = getFunctions(app);
      const initiate = httpsCallable(functions, "initiateClickToCall");
      await initiate({
        clientId: selectedClient,
        propertyId: selectedProperty,
        leadPhoneNumber: row.phoneNumber,
        leadName: row.name,
        leadId: row.id,
      });

      // Log click-to-call event (does NOT increment callCount — modal save handles that)
      await writeCallLog(selectedClient, selectedProperty, {
        type: "outbound-click-to-call",
        phoneNumber: row.phoneNumber,
        callerDocId: row.id,
        callerName: row.name,
        notes: "",
        agentId: userInfo?.id || null,
        agentInitials: buildInitials(userInfo),
        answered: null, // unknown until the call completes
        callSid: null,
      });

      // Open modal so agent can add notes after the call
      handleCallModalOpen(row);

      setCallSnack({
        open: true,
        message: `Calling office — press 1 to connect to ${row.name || formatPhoneNumber(row.phoneNumber)}`,
        severity: "success",
      });
    } catch (err) {
      console.error("Click-to-call error:", err);
      setCallSnack({
        open: true,
        message: err.message || "Failed to initiate call. Check sales phone configuration in Property Settings.",
        severity: "error",
      });
    } finally {
      setCallInProgress(null);
    }
  };

  // ── Derived stats from callLog ────────────────────────────────────────────

  const stats = useMemo(() => ({
    callsToday: todayLog.length,
    checkIns: checkinList.length,
    tours: tourList.length,
    outbound: todayLog.filter(
      (e) => e.type === "outbound-manual" || e.type === "outbound-click-to-call" || e.type === "new-lead"
    ).length,
  }), [todayLog, checkinList, tourList]);

  // ── Filtered rows ─────────────────────────────────────────────────────────

  const todayRows = useMemo(() => [
    ...checkinList.map((c) => ({ ...c, _category: "Check-in Today" })),
    ...tourList.map((c) => ({ ...c, _category: "Tour Today" })),
    ...callTodayList.map((c) => ({ ...c, _category: "Call Today" })),
  ], [checkinList, tourList, callTodayList]);

  const applySearch = (rows) => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (r) => r.name?.toLowerCase().includes(term) || r.phoneNumber?.includes(term)
    );
  };

  const filteredTodayRows = useMemo(() => {
    let rows = todayRows;
    if (sectionFilter === "Check-ins Today") rows = rows.filter((r) => r._category === "Check-in Today");
    else if (sectionFilter === "Tours Today") rows = rows.filter((r) => r._category === "Tour Today");
    else if (sectionFilter === "Calls Today") rows = rows.filter((r) => r._category === "Call Today");
    return applySearch(rows);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayRows, sectionFilter, searchTerm]);

  const filteredAllRows = useMemo(
    () => applySearch(allCalls),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCalls, searchTerm]
  );

  // ── Column definitions ────────────────────────────────────────────────────

  const callActionColumn = {
    field: "_call",
    headerName: "Call",
    width: 60,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Tooltip title={`Call ${params.row.name || "lead"}`}>
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!!callInProgress}
            onClick={(e) => handleClickToCall(e, params.row)}
          >
            {callInProgress === params.row.id
              ? <CircularProgress size={18} />
              : <PhoneIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    ),
  };

  const todayColumns = [
    callActionColumn,
    { field: "name", headerName: "Name", flex: 1.5, minWidth: 150 },
    {
      field: "phoneNumber", headerName: "Phone", flex: 1, minWidth: 150,
      renderCell: (p) => formatPhoneNumber(p.value),
    },
    {
      field: "_category", headerName: "Category", flex: 1, minWidth: 140,
      renderCell: (p) => (
        <Chip
          label={p.value}
          color={p.value === "Check-in Today" ? "success" : p.value === "Tour Today" ? "primary" : "warning"}
          size="small"
        />
      ),
    },
    { field: "callCount", headerName: "Times Called", flex: 0.8, minWidth: 110 },
    {
      field: "pastNotes", headerName: "Last Note", flex: 2, minWidth: 200,
      renderCell: (p) => getLastNote(p.value),
    },
  ];

  const allLeadsColumns = [
    callActionColumn,
    { field: "name", headerName: "Name", flex: 1.5, minWidth: 150 },
    {
      field: "phoneNumber", headerName: "Phone", flex: 1, minWidth: 140,
      renderCell: (p) => formatPhoneNumber(p.value),
    },
    { field: "nextCallDate", headerName: "Next Call", flex: 0.9, minWidth: 110 },
    { field: "tourDate", headerName: "Tour Date", flex: 0.9, minWidth: 110 },
    { field: "checkInDate", headerName: "Check-in", flex: 0.9, minWidth: 110 },
    { field: "callCount", headerName: "Calls", flex: 0.6, minWidth: 70 },
    {
      field: "pastNotes", headerName: "Last Note", flex: 2, minWidth: 200,
      renderCell: (p) => getLastNote(p.value),
    },
  ];

  const statBg = (color) => alpha(theme.palette[color]?.main || "#000", 0.1);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">Call List</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => setNonLeadCallModalOpen(true)}>
            New Non-Lead Call
          </Button>
          <Button variant="contained" onClick={() => handleCallModalOpen(null)}>
            New Lead
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        {[
          { label: "Calls Today", value: stats.callsToday, color: "primary" },
          { label: "Expected Check-ins", value: stats.checkIns, color: "success" },
          { label: "Expected Tours", value: stats.tours, color: "warning" },
          { label: "Outbound Calls", value: stats.outbound, color: "info" },
        ].map(({ label, value, color }) => (
          <Box key={label} sx={{ flex: 1, p: 2, bgcolor: statBg(color), borderRadius: 1, textAlign: "center" }}>
            <Typography variant="h4" color={`${color}.main`}>
              {callsLoading ? "—" : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* View tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)}>
          <Tab label="Today's Tasks" />
          <Tab label={`All Leads (${allCalls.length})`} />
        </Tabs>
      </Box>

      {/* Search + filter chips */}
      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Search" variant="outlined" size="small" value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Name or phone number"
          sx={{ flex: 1, minWidth: 250 }}
        />
        {viewTab === 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[
              { label: "All", filter: "All" },
              { label: `Check-ins Today (${stats.checkIns})`, filter: "Check-ins Today", color: "success" },
              { label: `Tours Today (${stats.tours})`, filter: "Tours Today", color: "primary" },
              { label: `Calls Today (${callTodayList.length})`, filter: "Calls Today", color: "warning" },
            ].map(({ label, filter, color }) => (
              <Chip
                key={filter}
                label={label}
                color={sectionFilter === filter ? (color || "primary") : "default"}
                onClick={() => setSectionFilter(filter)}
                clickable
              />
            ))}
          </Box>
        )}
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 600, width: "100%" }}>
        {viewTab === 0 ? (
          <DataGrid
            rows={filteredTodayRows}
            columns={todayColumns}
            loading={callsLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            onRowClick={(row) => handleCallModalOpen(row.row)}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        ) : (
          <DataGrid
            rows={filteredAllRows}
            columns={allLeadsColumns}
            loading={callsLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            onRowClick={(row) => handleCallModalOpen(row.row)}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        )}
      </Box>

      <CallModal
        isOpen={callModalOpen}
        onClose={handleCallModalClose}
        callerData={callerData}
        prefillPhone={prefillPhone}
        userInfo={userInfo}
      />

      <NonLeadCallModal
        isOpen={nonLeadCallModalOpen}
        onClose={() => setNonLeadCallModalOpen(false)}
        userInfo={userInfo}
      />

      <Snackbar
        open={callSnack.open}
        autoHideDuration={8000}
        onClose={() => setCallSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={callSnack.severity} onClose={() => setCallSnack((s) => ({ ...s, open: false }))}>
          {callSnack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CallList;
